import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import characterData from "@/data/character_data.json";

interface CharacterMapProps {
  onCharacterSetChange: (
    characters: string[],
    language: string,
    additionalSets: string[]
  ) => void;
  onNameChange: (name: string) => void;
  onSave?: () => void;
  onNext?: () => void;
  initialLanguage?: string;
  initialSets?: string[];
  initialName?: string;
}

// Transform the languages data to include a chars string
const LANGUAGES = Object.entries(characterData.languages).reduce((acc, [key, lang]) => {
  acc[key] = {
    name: lang.name,
    chars: lang.characters.map(c => c.char).join(''),
    count: lang.count
  };
  return acc;
}, {} as Record<string, { name: string; chars: string; count: number }>);

// Transform the additional sets data
const ADDITIONAL_SETS = Object.entries(characterData.additional_sets).reduce((acc, [key, set]) => {
  acc[key] = {
    name: set.name,
    chars: set.characters.map(c => c.char).join(''),
    description: set.description
  };
  return acc;
}, {} as Record<string, { name: string; chars: string; description: string }>);

export default function CharacterMap({ 
  onCharacterSetChange,
  onNameChange,
  onSave,
  onNext,
  initialLanguage = "english",
  initialSets = [],
  initialName = ""
}: CharacterMapProps) {
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    initialLanguage in LANGUAGES ? initialLanguage : "english"
  );
  const [cipherName, setCipherName] = useState(initialName);
  const [additionalSets, setAdditionalSets] = useState<Record<string, boolean>>(() => {
    const sets: Record<string, boolean> = {
      numbers: false,
      punctuation: false,
      brackets: false,
      math: false,
      currency: false,
      other: false
    };
    // Initialize from props
    initialSets.forEach(set => {
      if (set in sets) {
        sets[set] = true;
      }
    });
    return sets;
  });

  // Call onCharacterSetChange on mount to restore the state
  useEffect(() => {
    const validLanguage = selectedLanguage in LANGUAGES ? selectedLanguage : "english";
    updateCharacterSet(validLanguage, additionalSets);
  }, []);

  const updateCharacterSet = (language: string, newSets?: Record<string, boolean>) => {
    const validLanguage = language in LANGUAGES ? language : "english";
    const sets = newSets || additionalSets;
    let chars = LANGUAGES[validLanguage]?.chars ?? "";

    Object.entries(sets).forEach(([key, isSelected]) => {
      if (isSelected && key in ADDITIONAL_SETS) {
        const set = ADDITIONAL_SETS[key as keyof typeof ADDITIONAL_SETS];
        if (set) chars += set.chars;
      }
    });
    
    const selectedSets = Object.entries(sets)
      .filter(([_, isSelected]) => isSelected)
      .map(([key]) => key);
    
    onCharacterSetChange(
      Array.from(new Set(chars.split(""))),
      validLanguage,
      selectedSets
    );
  };

  const handleLanguageChange = (value: string) => {
    const validLanguage = value in LANGUAGES ? value : "english";
    setSelectedLanguage(validLanguage);
    updateCharacterSet(validLanguage, additionalSets);
  };

  const handleSetToggle = (set: string, checked: boolean) => {
    const newSets = { ...additionalSets, [set]: checked };
    setAdditionalSets(newSets);
    updateCharacterSet(selectedLanguage, newSets);
  };

  return (
    <div className="space-y-4">
      {/* Section 1: Name */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-gray-900">1. Name Your Cipher</h4>
        <Input
          id="cipher-name"
          placeholder="Enter a name for your cipher..."
          value={cipherName}
          onChange={(e) => {
            setCipherName(e.target.value);
            onNameChange(e.target.value);
          }}
          maxLength={36}
        />
        <p className="text-sm text-muted-foreground">
          {cipherName.length}/36 characters
        </p>
      </div>

      {/* Section 2: Character Set */}
      <div className="pt-3 border-t space-y-4">
        <div>
          <h4 className="text-sm font-semibold text-gray-900">2. Select Character Set</h4>
          <p className="text-sm text-muted-foreground mt-1">
            Choose a language and additional characters to include in your cipher
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="language">Base Language</Label>
          <Select value={selectedLanguage} onValueChange={handleLanguageChange}>
            <SelectTrigger>
              <SelectValue placeholder="Select a language" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(LANGUAGES).map(([key, { name, count }]) => (
                <SelectItem key={key} value={key}>
                  {name} ({count} characters)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-3">
          <Label>Additional Character Sets</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(ADDITIONAL_SETS).map(([key, { name, description }]) => (
              <div key={key} className="flex items-start space-x-2">
                <Checkbox
                  id={key}
                  checked={additionalSets[key]}
                  onCheckedChange={(checked) =>
                    handleSetToggle(key, checked as boolean)
                  }
                />
                <div className="grid gap-1.5 leading-none">
                  <Label
                    htmlFor={key}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    {name}
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
} 